import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import DatePicker, { registerLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import "react-datepicker/dist/react-datepicker.css";
import "../DatePickerCustomStyles.css";
import {
  addGeneralDesignReport,
  addSketchDesignReport,
} from "../../../../api/reportsApi";
import { UserContext } from "../../../../contexts/CurrentUserContext.js";
import Preloader from "../../../Preloader/Preloader.js";
import Select from "react-select";
import { getSketchDesignEstimateWorkTypes } from "../../../../api/projectEstimatesApi";
import { getSketchDesignProjects } from "../../../../api/projectApi";
import { getRpGpLaborEffortId } from "../../../../api/paymentsProgressApi";
import "./SketchDesignReport.css";

registerLocale("ru", ru);

// Проекты: объединяются из:
// - GET /sketch_design/tei/project/all → [{ id, name }]
// - GET /general_design/all → [{ project: { id, name }, ... }]
// Для проектов из general_design вид работ не требуется.
// Виды работ по проекту: GET /sketch_design/estimate/work_types/get/{project_id} → [{ id, name, status, ... }]
// При status === "completed" добавление отчётов по этому виду работ запрещено

const SKETCH_DESIGN_DEPARTMENT_ID = 4;

function SketchDesignReport() {
  const navigate = useNavigate();
  const { userData } = useContext(UserContext);

  const initialFormData = useMemo(
    () => ({
      id: 1,
      selectedDate: new Date(),
      comment: "",
      department: "",
      workType: "",
      project: "",
      effort: "00:15",
      availableWorkTypes: [],
      is_remote: false,
    }),
    [],
  );

  const [sections, setSections] = useState([]);
  const [formError, setFormError] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatuses, setFormStatuses] = useState({});

  const [sketchProjects, setSketchProjects] = useState([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [projectsLoadError, setProjectsLoadError] = useState(null);
  const [projectsPeriod, setProjectsPeriod] = useState(() => {
    const now = new Date();
    return { month: now.getMonth() + 1, year: now.getFullYear() };
  });

  const sketchDepartment = useMemo(
    () =>
      userData?.departments?.find((d) => d.id === SKETCH_DESIGN_DEPARTMENT_ID),
    [userData],
  );

  const allGroups = useMemo(
    () => sketchDepartment?.groups || [],
    [sketchDepartment],
  );

  useEffect(() => {
    const selectedDate = sections[0]?.selectedDate;
    if (!(selectedDate instanceof Date) || Number.isNaN(selectedDate.getTime())) {
      return;
    }
    const month = selectedDate.getMonth() + 1;
    const year = selectedDate.getFullYear();
    setProjectsPeriod((prev) =>
      prev.month === month && prev.year === year ? prev : { month, year },
    );
  }, [sections]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setProjectsLoaded(false);
        setProjectsLoadError(null);
        const list = await getSketchDesignProjects(
          projectsPeriod.month,
          projectsPeriod.year,
        );
        if (!cancelled) {
          setSketchProjects(Array.isArray(list) ? list : []);
        }
      } catch (err) {
        console.error("Ошибка загрузки проектов ЭП:", err);
        if (!cancelled) {
          setProjectsLoadError(err?.message || "Не удалось загрузить проекты");
          setSketchProjects([]);
        }
      } finally {
        if (!cancelled) setProjectsLoaded(true);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [projectsPeriod.month, projectsPeriod.year]);

  useEffect(() => {
    if (!userData?.departments) return;
    const dept = userData.departments.find(
      (d) => d.id === SKETCH_DESIGN_DEPARTMENT_ID,
    );
    const defaultGroup = dept?.groups?.length === 1 ? dept.groups[0].name : "";
    setSections([
      {
        ...initialFormData,
        department: defaultGroup,
      },
    ]);
    setIsLoading(false);
  }, [userData, initialFormData]);

  const getProjectMeta = useCallback(
    (selectedProjectKey) =>
      sketchProjects.find((p) => {
        const optionValue =
          p.source === "general_design"
            ? `gd-${p.id}`
            : `sk-${p.id}`;
        return optionValue === selectedProjectKey;
      }) || null,
    [sketchProjects],
  );

  const isGeneralDesignProject = useCallback(
    (selectedProjectKey) =>
      getProjectMeta(selectedProjectKey)?.source === "general_design",
    [getProjectMeta],
  );

  const loadWorkTypesForProject = useCallback(async (selectedProjectKey) => {
    const projectMeta = getProjectMeta(selectedProjectKey);
    const projectId = Number(projectMeta?.id);
    if (!projectId) return [];
    try {
      const raw = await getSketchDesignEstimateWorkTypes(projectId);
      const list = Array.isArray(raw) ? raw : (raw?.work_types ?? []);
      const items = Array.isArray(list) ? list : [];
      // Не показываем виды работ со status = "completed" — по ним запрещено добавлять отчёты
      return items.filter(
        (wt) => (wt.status || "").toLowerCase() !== "completed",
      );
    } catch (err) {
      console.error("Ошибка загрузки видов работ по проекту:", err);
      return [];
    }
  }, [getProjectMeta]);

  // Ключ для эффекта: при смене проекта в любой секции — перезагружаем виды работ по всем секциям
  const sectionProjectKey = sections
    .map((s) => `${s.id}:${s.project || ""}`)
    .join("|");
  useEffect(() => {
    const sectionsWithProject = sections.filter(
      (s) => s.project && !isGeneralDesignProject(s.project),
    );
    if (!sectionsWithProject.length) return;
    let cancelled = false;
    Promise.all(
      sectionsWithProject.map(async (section) => ({
        sectionId: section.id,
        workTypes: await loadWorkTypesForProject(section.project),
      })),
    ).then((results) => {
      if (cancelled) return;
      setSections((prev) =>
        prev.map((s) => {
          const result = results.find((r) => r.sectionId === s.id);
          if (!result) return s;
          const currentWorkTypeValid = result.workTypes.some(
            (wt) => String(wt.id) === String(s.workType),
          );
          return {
            ...s,
            availableWorkTypes: result.workTypes,
            workType: currentWorkTypeValid ? s.workType : "",
          };
        }),
      );
    });
    return () => {
      cancelled = true;
    };
    // sectionProjectKey выводится из sections; не используем sections в deps, чтобы не перезапускать эффект при обновлении только availableWorkTypes
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sectionProjectKey достаточен для синхронизации загрузки по проектам
  }, [sectionProjectKey, isGeneralDesignProject, loadWorkTypesForProject]);

  const handleFieldChange = useCallback((sectionId, field, value) => {
    setSections((prev) =>
      prev.map((s) => (s.id === sectionId ? { ...s, [field]: value } : s)),
    );
  }, []);

  const handleProjectChange = useCallback(
    (sectionId, projectId) => {
      const isGeneral = isGeneralDesignProject(projectId);
      setSections((prev) =>
        prev.map((s) =>
          s.id === sectionId
            ? {
                ...s,
                project: projectId,
                workType: "",
                availableWorkTypes: isGeneral ? [] : s.availableWorkTypes,
              }
            : s,
        ),
      );
    },
    [isGeneralDesignProject],
  );

  const addSection = () => {
    const newId = sections.length + 1;
    setSections((prev) => [
      ...prev,
      {
        ...initialFormData,
        id: newId,
        department: allGroups.length === 1 ? allGroups[0].name : "",
      },
    ]);
  };

  const removeSection = (id) => {
    if (sections.length > 1) {
      setSections((prev) => prev.filter((s) => s.id !== id));
    }
  };

  const createRegularEffortOptions = () => {
    const options = [];
    for (let hours = 0; hours <= 8; hours++) {
      for (let minutes = 0; minutes < 60; minutes += 15) {
        if (hours === 0 && minutes === 0) continue;
        if (hours === 8 && minutes > 0) break;
        const value = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
        options.push({ value, label: value });
      }
    }
    return options;
  };

  const handleEffortChange = (sectionId, selectedOption) => {
    handleFieldChange(sectionId, "effort", selectedOption.value);
  };

  const handleSliderChange = (sectionId, value) => {
    value = Math.round(value / 15) * 15;
    if (value < 15) value = 15;
    if (value > 480) value = 480;
    const hours = Math.floor(value / 60);
    const minutes = value % 60;
    const formattedValue = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    handleFieldChange(sectionId, "effort", formattedValue);
  };

  const isFormValid = () =>
    sections.every(
      (s) => {
        const isGeneral = isGeneralDesignProject(s.project);
        return (
        s.selectedDate &&
        s.department &&
        s.project &&
        (isGeneral || s.workType) &&
        s.effort !== "00:00"
        );
      },
    );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) return;

    setIsSubmitting(true);
    setFormError("");
    setFormStatuses({});

    try {
      for (const [index, section] of sections.entries()) {
        const selectedDate = new Date(section.selectedDate);
        selectedDate.setHours(12, 0, 0, 0);
        const datetime = selectedDate.toLocaleDateString("en-CA");
        const [hours, minutes] = section.effort.split(":").map(Number);
        const secondsSpend = hours * 3600 + minutes * 60;
        const isGeneralProject = isGeneralDesignProject(section.project);

        const projectMeta = getProjectMeta(section.project);
        const projectId = Number(projectMeta?.id);
        const workTypeId = parseInt(section.workType, 10);
        const laborEffortId = getRpGpLaborEffortId(projectMeta);

        if (
          !isGeneralProject &&
          (Number.isNaN(workTypeId) || workTypeId <= 0)
        ) {
          setFormError("Выберите вид работ");
          setIsSubmitting(false);
          return;
        }
        if (Number.isNaN(projectId) || projectId <= 0) {
          setFormError("Выберите проект");
          setIsSubmitting(false);
          return;
        }
        if (secondsSpend <= 0) {
          setFormError("Укажите трудозатраты больше 0");
          setIsSubmitting(false);
          return;
        }

        setFormStatuses((prev) => ({
          ...prev,
          [index]: { status: "sending", message: "Отправка..." },
        }));

        const baseBody = {
          datetime,
          comment: String(section.comment || ""),
          seconds_spend: secondsSpend,
          is_remote: Boolean(section.is_remote || false),
        };

        if (isGeneralProject) {
          const generalBody = {
            datetime,
            project_id: projectId,
            seconds_spend: secondsSpend,
            comment: String(section.comment || ""),
          };
          if (laborEffortId) {
            generalBody.rp_gp_labor_effort_id = laborEffortId;
          }
          await addGeneralDesignReport(generalBody);
        } else {
          await addSketchDesignReport(projectId, {
            ...baseBody,
            work_type_id: workTypeId,
          });
        }

        setFormStatuses((prev) => ({
          ...prev,
          [index]: { status: "success", message: "Успешно отправлено" },
        }));
      }

      setShowPopup(true);
      setTimeout(() => {
        setShowPopup(false);
        setIsSubmitting(false);
        navigate("/report/edit");
      }, 4000);
    } catch (err) {
      setFormError(`Ошибка при отправке: ${err.message}`);
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSections([
      {
        ...initialFormData,
        department: allGroups.length === 1 ? allGroups[0].name : "",
      },
    ]);
    setFormError("");
    setShowPopup(false);
  };

  const effortSelectStyles = {
    container: (provided) => ({
      ...provided,
      width: "120px",
      flexShrink: 0,
    }),
    control: (provided, state) => ({
      ...provided,
      width: "120px",
      maxWidth: "120px",
      minHeight: "unset",
      height: "38px",
      borderRadius: "10px",
      border: "1px solid #E0E0E0",
      fontFamily: "Manrope",
      fontSize: "14px",
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: "38px",
      padding: "0 8px",
    }),
    singleValue: (provided) => ({ ...provided, margin: 0 }),
    placeholder: (provided) => ({ ...provided, margin: 0 }),
  };

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: "10px",
      border: "1px solid #E0E0E0",
      padding: "5px",
      fontFamily: "Manrope",
      fontSize: "14px",
      fontWeight: 300,
      width: "100%",
      backgroundColor: state.isDisabled ? "#f5f5f5" : "white",
      cursor: state.isDisabled ? "not-allowed" : "pointer",
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "10px",
      marginTop: "5px",
      zIndex: 1000,
    }),
  };

  if (isLoading || !userData) {
    return <Preloader />;
  }

  if (!sketchDepartment || !allGroups.length) {
    return (
      <p className="sketch-report-error">
        Нет доступа к отделу Эскизного проектирования.
      </p>
    );
  }

  if (projectsLoadError) {
    return <p className="sketch-report-error">{projectsLoadError}</p>;
  }

  if (projectsLoaded && sketchProjects.length === 0) {
    return (
      <p className="sketch-report-error">
        Нет проектов Эскизного проектирования для отчётов.
      </p>
    );
  }

  return (
    <section
      className={`sketch-design-report ${isSubmitting || showPopup ? "submitting" : ""}`}
    >
      <div className="sketch-design-report__form">
        {sections.map((section, index) => (
          <div key={section.id} className="sketch-design-report__section">
            {sections.length > 1 && (
              <div className="sketch-design-report__section-header">
                <h3>Отчет {index + 1}</h3>
                <button
                  type="button"
                  onClick={() => removeSection(section.id)}
                  className="sketch-design-report__remove"
                  disabled={isSubmitting}
                  aria-label="Удалить секцию"
                >
                  &times;
                </button>
              </div>
            )}
            {formStatuses[index] && (
              <div
                className={`sketch-design-report__status sketch-design-report__status--${formStatuses[index].status}`}
              >
                {formStatuses[index].message}
              </div>
            )}

            <div className="sketch-design-report__columns">
              <div className="sketch-design-report__col sketch-design-report__col--left">
                <div className="sketch-design-report__field">
                  <label className="sketch-design-report__label">
                    Отдел{" "}
                    <span className="sketch-design-report__required">*</span>
                  </label>
                  {allGroups.length === 1 ? (
                    <input
                      type="text"
                      className="sketch-design-report__input"
                      value={allGroups[0].name}
                      readOnly
                      disabled
                    />
                  ) : (
                    <Select
                      value={
                        section.department
                          ? {
                              value: section.department,
                              label: section.department,
                            }
                          : null
                      }
                      onChange={(opt) =>
                        handleFieldChange(section.id, "department", opt?.value)
                      }
                      options={allGroups.map((g) => ({
                        value: g.name,
                        label: g.name,
                      }))}
                      styles={selectStyles}
                      placeholder="Выберите отдел"
                      noOptionsMessage={() => "Нет отделов"}
                      isDisabled={isSubmitting}
                    />
                  )}
                </div>

                <div className="sketch-design-report__field">
                  <label className="sketch-design-report__label">
                    Проект{" "}
                    <span className="sketch-design-report__required">*</span>
                  </label>
                  <Select
                    value={
                      section.project
                        ? {
                            value: section.project,
                            label:
                              (() => {
                                const selectedProject = getProjectMeta(
                                  section.project,
                                );
                                if (!selectedProject) return "";
                                if (
                                  selectedProject.source === "general_design" &&
                                  selectedProject.section_name
                                ) {
                                  return `${selectedProject.name} / ${selectedProject.section_name}`;
                                }
                                return selectedProject.name;
                              })(),
                          }
                        : null
                    }
                    onChange={(opt) =>
                      handleProjectChange(section.id, opt?.value)
                    }
                    options={sketchProjects.map((p) => ({
                      value:
                        p.source === "general_design"
                          ? `gd-${p.id}`
                          : `sk-${p.id}`,
                      label:
                        p.source === "general_design" && p.section_name
                          ? `${p.name} / ${p.section_name}`
                          : p.name,
                    }))}
                    styles={selectStyles}
                    placeholder={
                      projectsLoaded ? "Выберите проект" : "Загрузка..."
                    }
                    noOptionsMessage={() => "Нет проектов"}
                    isDisabled={isSubmitting || !projectsLoaded}
                  />
                </div>

                <div className="sketch-design-report__field">
                  <label className="sketch-design-report__label">
                    Комментарий
                  </label>
                  <textarea
                    className="sketch-design-report__textarea"
                    value={section.comment}
                    onChange={(e) =>
                      handleFieldChange(section.id, "comment", e.target.value)
                    }
                    placeholder="Введите комментарий"
                    disabled={isSubmitting}
                    rows={4}
                  />
                </div>
              </div>

              <div className="sketch-design-report__col sketch-design-report__col--right">
                <div className="sketch-design-report__field">
                  <label className="sketch-design-report__label">
                    Дата{" "}
                    <span className="sketch-design-report__required">*</span>
                  </label>
                  <div className="sketch-design-report__datepicker-wrap">
                    <DatePicker
                      selected={section.selectedDate}
                      onChange={(date) =>
                        handleFieldChange(section.id, "selectedDate", date)
                      }
                      className="sketch-design-report__input sketch-design-report__datepicker"
                      dateFormat="dd.MM.yyyy"
                      placeholderText="Выберите дату"
                      locale="ru"
                      calendarStartDay={1}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="sketch-design-report__field">
                  <label className="sketch-design-report__label">
                    Вид работ{" "}
                    <span className="sketch-design-report__required">*</span>
                  </label>
                  <Select
                    value={
                      section.workType && section.availableWorkTypes?.length
                        ? (() => {
                            const wt = section.availableWorkTypes.find(
                              (w) => w.id?.toString() === section.workType,
                            );
                            return wt
                              ? { value: String(wt.id), label: wt.name }
                              : null;
                          })()
                        : null
                    }
                    onChange={(opt) =>
                      handleFieldChange(section.id, "workType", opt?.value)
                    }
                    options={(section.availableWorkTypes || []).map((wt) => ({
                      value: String(wt.id),
                      label: wt.name,
                    }))}
                    styles={selectStyles}
                    placeholder={
                      isGeneralDesignProject(section.project)
                        ? "Для этого проекта не требуется"
                        : "Сначала выберите проект и отдел"
                    }
                    noOptionsMessage={() => "Нет видов работ"}
                    isDisabled={
                      isSubmitting ||
                      !section.project ||
                      !section.department ||
                      isGeneralDesignProject(section.project)
                    }
                  />
                </div>

                <div className="sketch-design-report__field sketch-design-report__effort">
                  <label className="sketch-design-report__label">
                    Трудозатраты
                  </label>
                  <div className="sketch-design-report__effort-row">
                    <input
                      type="range"
                      min={15}
                      max="480"
                      step={15}
                      value={section.effort
                        .split(":")
                        .reduce((acc, t) => acc * 60 + parseInt(t, 10), 0)}
                      onChange={(e) =>
                        handleSliderChange(
                          section.id,
                          parseInt(e.target.value, 10),
                        )
                      }
                      className="sketch-design-report__slider"
                      disabled={isSubmitting}
                    />
                    <Select
                      value={
                        createRegularEffortOptions().find(
                          (o) => o.value === section.effort,
                        ) || null
                      }
                      onChange={(opt) => handleEffortChange(section.id, opt)}
                      options={createRegularEffortOptions()}
                      styles={effortSelectStyles}
                      placeholder="Время"
                      noOptionsMessage={() => "Нет вариантов"}
                      isDisabled={isSubmitting}
                      isSearchable={false}
                    />
                  </div>
                  <div className="sketch-design-report__checkboxes">
                    <label className="sketch-design-report__checkbox-label">
                      <input
                        type="checkbox"
                        checked={section.is_remote || false}
                        onChange={(e) =>
                          handleFieldChange(
                            section.id,
                            "is_remote",
                            e.target.checked,
                          )
                        }
                        disabled={isSubmitting}
                      />
                      Удаленная работа
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showPopup && (
        <div className="sketch-design-report__popup-overlay">
          <div className="sketch-design-report__popup">
            <div className="sketch-design-report__popup-icon" />
            <p className="sketch-design-report__popup-text">
              Форма успешно отправлена
            </p>
          </div>
        </div>
      )}

      {formError && (
        <p className="sketch-design-report__form-error">{formError}</p>
      )}

      <div className="sketch-design-report__buttons">
        <button
          type="button"
          className="sketch-design-report__btn sketch-design-report__btn--add"
          onClick={addSection}
          disabled={isSubmitting}
          aria-label="Добавить отчёт"
        >
          +
        </button>
        <button
          type="button"
          className={`sketch-design-report__btn sketch-design-report__btn--submit ${
            isFormValid() ? "sketch-design-report__btn--submit-active" : ""
          }`}
          onClick={handleSubmit}
          disabled={isSubmitting || !isFormValid()}
        >
          Отправить
        </button>
        <button
          type="button"
          className="sketch-design-report__btn sketch-design-report__btn--reset"
          onClick={resetForm}
          disabled={isSubmitting}
        >
          Сбросить
        </button>
      </div>
    </section>
  );
}

export default SketchDesignReport;
